import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, HardDrive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, Spinner } from "@/components/Field";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const SetupSteps = ({ uri }) => (
  <ol data-testid="drive-setup-steps" className="list-decimal pl-5 space-y-1 text-sm text-slate-600">
    <li>Su console.cloud.google.com crea un progetto e attiva «Google Drive API».</li>
    <li>In «Schermata consenso OAuth» scegli «Esterno», poi premi «Pubblica app» (In produzione): così il collegamento non scade dopo 7 giorni.</li>
    <li>In «Credenziali» crea un «ID client OAuth» di tipo «Applicazione web» con questo URI di reindirizzamento autorizzato:
      <code data-testid="drive-redirect-uri" className="block mt-1 break-all rounded bg-slate-100 px-2 py-1 text-slate-800">{uri}</code>
    </li>
    <li>Inserisci Client ID e Client Secret nell'app (te li chiedo io in un riquadro sicuro).</li>
  </ol>
);

export const DriveCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/drive/status").then((r) => setS(r.data)).catch(() => setS({}));

  useEffect(() => {
    load();
    const r = params.get("drive");
    if (r === "ok") toast.success("Google Drive collegato");
    if (r === "error") toast.error(params.get("msg") || "Collegamento non riuscito");
    if (r) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try { window.location.href = (await api.post("/drive/connect")).data.authorization_url; } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Drive? I file restano nel Drive, ma l'app non li vedrà più.")) return;
    setBusy(true);
    try { await api.post("/drive/disconnect"); toast.success("Google Drive scollegato"); await load(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  if (!s) return <Card className="p-5"><Spinner /></Card>;
  return (
    <Card testId="settings-drive" className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <HardDrive className="h-6 w-6 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-semibold">Google Drive</h2>
            <p className="text-sm text-slate-500">Fatture e scontrini salvati in automatico; la cartella «Documenti» è visibile ai membri nell'app.</p>
          </div>
        </div>
        <StatusBadge label={s.connected ? (s.status === "error" ? "Da ricollegare" : "Collegato") : "Non collegato"} tone={s.connected ? (s.status === "error" ? "danger" : "success") : undefined} />
      </div>
      {!s.configured && <SetupSteps uri={s.redirect_uri} />}
      {s.connected && (
        <div data-testid="drive-connection-info" className="text-sm text-slate-700 space-y-1">
          <div>Account: <strong>{s.email}</strong></div>
          <div>Collegato da {s.connected_by_name || "—"} il {fmtDateTime(s.connected_at)}</div>
          {s.error && <div className="text-red-700">{s.error}</div>}
          <a data-testid="drive-open-folder" href={s.folder_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">Apri la cartella in Google Drive <ExternalLink className="h-4 w-4" /></a>
        </div>
      )}
      {s.configured && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" data-testid="drive-connect" className="h-11" disabled={busy} onClick={connect}>{s.connected ? "Ricollega / cambia account" : "Collega Google Drive"}</Button>
          {s.connected && <Button type="button" data-testid="drive-disconnect" variant="outline" className="h-11 text-red-700" disabled={busy} onClick={disconnect}>Scollega</Button>}
        </div>
      )}
    </Card>
  );
};
